import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { Grid } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import BasicDialog from "../../base/BasicDialog";
import LoaderWrapper from "../../wrapper/LoaderWrapper";
import TextFieldFormControl from "../../field/TextFieldFormControl";

import { useForm } from "../../../hooks/useForm";

import { useAsync, useAsyncFn } from "../../../hooks/useAsync";
import { useSnackbarAlert } from "../../../context/snackbarAlert";

import useEquipmentService from "../../../services/equipmentService";

import { getErrorMsg } from "../../../helpers/methods";
import FormulaService from "../../../services/formulaService";
import DateService from "../../../services/dateService";

import {
  DIALOG_PREVIEW_MODE,
  DIALOG_EDIT_MODE,
  DIALOG_CREATE_MODE,
} from "../../../helpers/constants";

import EquipmentUsageForSchemeForm from "./EquipmentUsageForSchemeForm";

const REQUIRED_FIELDS = ["equipment_usages"];

const FIELDS_TO_SAVE = ["id", ...REQUIRED_FIELDS];

const EquipmentInstalationTenantSetUsageForSchemeDialog = (props) => {
  const { t } = useTranslation();
  const snackbarAlert = useSnackbarAlert();

  const isReadOnly = useMemo(
    () => props.readOnly || props.dialogMode === DIALOG_PREVIEW_MODE,
    [props.readOnly, props.dialogMode],
  );

  const { formValue, setFormValue } = useForm();
  const [bmsStart, setBmsStart] = useState();
  const [bmsEnd, setBmsEnd] = useState();

  const {
    formValue: equipmentUsages,
    setFormValue: setEquipmentUsages,
    onChangeAutocompleteFieldWithObjectOptionsInArrayForm,
    onChangeInArrayForm,
  } = useForm();

  const {
    getEquipmentInstalationTenantSetUsageForSchemeDetails,
    updateEquipmentInstalationTenantSetUsageForScheme,
  } = useEquipmentService();

  const updateEitsUsageForSchemeFn = useAsyncFn(
    updateEquipmentInstalationTenantSetUsageForScheme,
  );

  const eitsCalculatedUsage = useMemo(() => {
    if (!formValue || !equipmentUsages) return "";

    let equipmentIdToUsageMapping = {};

    for (const equipmentUsage of equipmentUsages) {
      equipmentIdToUsageMapping[equipmentUsage.equipment] =
        equipmentUsage.estseu_usage_value;
    }

    return FormulaService.calculateFormula(
      formValue.equipscheme_scripts,
      equipmentIdToUsageMapping,
    ).toFixed(2);
  }, [equipmentUsages]);

  const initialData = useAsync(() => {
    return getEquipmentInstalationTenantSetUsageForSchemeDetails(
      props.eitsUsageId,
    );
  }, [props.eitsUsageId, props.dialogMode]);

  useEffect(() => {
    setFormValue(initialData.value);
    setEquipmentUsages(initialData?.value?.equipment_usages);
    setBmsStart(
      DateService.getISOStringZeroHourOfDate(
        DateService.getMonthBehind(1, initialData?.value?.eits_usage_period),
      ),
    );
    setBmsEnd(
      DateService.getISOStringleEndHourOfDate(
        DateService.getMonthBehind(-2, initialData?.value?.eits_usage_period),
      ),
    );
  }, [initialData.loading]);

  const prepareDataToSend = () => {
    const dataToSend = {};
    dataToSend.equipment_usages = equipmentUsages;

    return dataToSend;
  };

  const handleSubmit = (dataToSend) => {
    updateEitsUsageForSchemeFn
      .execute(dataToSend, props.eitsUsageId)
      .then((res) => {
        snackbarAlert.openSuccessSnackbarAlert(
          t("snackbar_alert.eits_usage_updated"),
        );

        if (props.onSubmitCallback) {
          props.onSubmitCallback();
        }
        props.onClose();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(
          getErrorMsg(
            error.data,
            t("snackbar_alert.occurred_error_during_saving_changes"),
          ),
        );
      });
  };

  const isFormValid = true;

  const isLoading = formValue === undefined || equipmentUsages === undefined;

  const getDialogContent = () => {
    if (isLoading) return <LoaderWrapper showLoader={true} />;

    return (
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        rowGap={1}
        columnSpacing={2}
      >
        <Grid item xs={12} sm={9}>
          <TextFieldFormControl
            value={formValue.equipscheme_scripts_converted}
            label={t("dialog.equipment.equipscheme_scripts")}
            readOnly={false}
            onChange={() => {}}
            multiline={true}
            resize={"vertical"}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextFieldFormControl
            value={eitsCalculatedUsage}
            label={t("dialog.eits_usage.eits_usage_value")}
            readOnly={false}
            onChange={() => {}}
            multiline={true}
          />
        </Grid>

        <Grid item xs={12} sm={12}>
          {equipmentUsages.map((equipmentUsage, index) => (
            <EquipmentUsageForSchemeForm
              bmsStart={bmsStart}
              bmsEnd={bmsEnd}
              index={index}
              formValue={equipmentUsage}
              onChange={onChangeInArrayForm}
              onChangeAutocompleteFieldWithObjectOptions={
                onChangeAutocompleteFieldWithObjectOptionsInArrayForm
              }
              setFormValue={setEquipmentUsages}
            />
          ))}
        </Grid>
        {!isReadOnly && (
          <Grid item xs={12}>
            <LoadingButton
              variant="contained"
              color="primary"
              fullWidth
              loading={updateEitsUsageForSchemeFn.loading}
              disabled={!isFormValid}
              onClick={() => handleSubmit(prepareDataToSend())}
            >
              {t("save")}
            </LoadingButton>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign="center"
      contentAlign="center"
      showTopFullScreenButton={false}
      title={t("dialog.equipment.select_usage_for_scheme")}
      maxWidth="lg"
      showDialogActions
    >
      {getDialogContent()}
    </BasicDialog>
  );
};

EquipmentInstalationTenantSetUsageForSchemeDialog.propTypes = {
  dialogMode: PropTypes.oneOf([
    DIALOG_CREATE_MODE,
    DIALOG_EDIT_MODE,
    DIALOG_PREVIEW_MODE,
  ]),
  readOnly: PropTypes.bool,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmitCallback: PropTypes.func,
  fieldsToSave: PropTypes.array,
};

EquipmentInstalationTenantSetUsageForSchemeDialog.defaultProps = {
  open: false,
};

export default EquipmentInstalationTenantSetUsageForSchemeDialog;
